import React, { useEffect, useState } from "react";
import { Sync } from "@mui/icons-material";
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import * as FileSaver from "file-saver";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import * as XLSX from "xlsx";
import { axiosClient } from "../../../Constant/apiConstant";
import {
  swalErrorMessage,
  swalWarningMessage,
} from "../../../Constant/swalMessage";
import {
  converTime,
  getCallStatusData,
  hasAllProjectAccess,
  hasCallHistoryAccess,
  isEmpty,
  PAGE_SIZE_50,
} from "../CommonComponents/Common";
import {
  getCallHistoryAction,
  getCallHistoryCountAction,
} from "../ProjectSection/CampaignSection/Store/Campaign.action";
import CallHistoryHeader from "./CallHistoryHeader";
import { ReactComponent as CopyIcon } from "../../../assets/Images/CopyIcon.svg";
import { ReactComponent as RedirectIcon } from "../../../assets/icons/external-link.svg";
import chroma from "chroma-js";
import copy from "copy-to-clipboard";
import CallTranscriptModal from "./CallTranscriptModal";
import DynamicTable from "../CommonComponents/Table/DynamicTable";
import CallHistoryPopover from "./CallHistoryPopover";
import CallLogs from "../Campaigns/CallHistory/CallLogs";
import FilterBody from "../../../common/components/Filter/FilterBody";
import ReactAudioPlayer from "react-audio-player";
import CommonPopup from "../CommonComponents/PopUpCards/CommonPopup";
import { ReactComponent as ExportFileIcon } from "../../../assets/Images/ExportPopupIcon.svg";
import StatusButton from "../Common/Buttons/StatusButton";
import BaseButton from "../Common/Buttons/BaseButton";
import { shortenString } from "../../../Constant/helper";
import moment1 from "moment-timezone";
import "../../../assets/css/page/CallHistory.scss";
import { getDecryptedItem } from "../../../utils/localStorageHelper";
import { Bot, MoveLeft } from "lucide-react";
import { User } from "lucide-react";
import { CopyAlert } from "../CommonComponents/CommonComponents";

const CallDirectionIcon = ({ callType }) => {
  const type = callType?.toUpperCase();
  const typeColorMap = {
    REDIAL: "#f19c2cff",
    OUTBOUND: "#ffad1f",
    INBOUND: "#56c819",
    OUTBOUND_INBOUND: "#56c819",
  };

  const baseColor = typeColorMap[type];
  if (!baseColor) return null;
  const bgColor = chroma(baseColor).alpha(0.3).hex();
  const rotation = type === "OUTBOUND" ? 135 : -45;
  const tooltipTitle = type === "OUTBOUND_INBOUND" ? "SIP Inbound" : type;

  return (
    <Tooltip title={tooltipTitle}>
      <Box
        className="flex-xy-center"
        sx={{
          width: 27,
          height: 27,
          borderRadius: "50%",
          backgroundColor: bgColor,
        }}
      >
        {type === "REDIAL" ? (
          <Sync style={{ color: baseColor, fontSize: 20 }} />
        ) : (
          <MoveLeft
            size={16}
            style={{
              color: baseColor,
              transform: `rotate(${rotation}deg)`,
            }}
          />
        )}
      </Box>
    </Tooltip>
  );
};

const CallHistory = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const currentDate = moment().format("YYYY-MM-DD");
  const location = useLocation();
  const locSearch = new URLSearchParams(location.search);
  let Stalker = locSearch.get("stalker");
  let trunk = locSearch.get("trunk");
  const timezone = moment1.tz.guess();

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showCallTranscriptModal, setShowCallTranscriptModal] = useState(false);
  const [selectedTranscript, setSelectedTranscript] = useState(null) as any;
  const [lastViewedTranscriptUUID, setLastViewedTranscriptUUID] =
    useState(null);
  const [searchText, setSearchText] = useState(undefined);
  const [isReady, setIsReady] = useState(false);
  const [filterName, setFilterName] = useState("");
  const [filterNameFromDropdown, setFilterNameFromDropdown] = useState("");
  const [dateRange, setDateRange] = useState([
    new Date(currentDate),
    new Date(currentDate),
  ]);
  const [exportLoad, setExportLoad] = useState(false);
  const [startDate, endDate] = dateRange;

  const reducer: any = useSelector((state) => state);
  const {
    ProjectReducer: { projectList },
    AccountReducer: { constantData, selectedAccount, accountRoles },
    CampaignReducer: { callHistoryDetails, callHistoryDetailsTotalCounts },
  } = reducer;

  const hasRestrictedCallHistoryAccess = hasCallHistoryAccess(accountRoles);
  const hasAllProjectsAccess = hasAllProjectAccess(accountRoles);
  const accessedProjectIds =
    (hasRestrictedCallHistoryAccess || hasAllProjectsAccess) &&
    projectList?.map((p) => p._id);

  const [call_uuid, setCall_uuid] = useState("");
  const [show, setShow] = useState(false);
  const [searchTerm, setSearchTerm] = useState(
    getDecryptedItem("callHistorySearchTerm") || ""
  );
  const [exportPopup, setExportPopup] = useState(false);
  const [copyAlert, setCopyAlert] = useState({
    open: false,
    message: "",
  });

  const [state, setState] = useState<any>({
    filterDrawer: false,
    trackEnabledRecording: [],
    pageIndex: currentPage,
    durationFilter: "",
    statusFilter: [],
    callTypeFilter: [],
    outComefilter: [],
    min: "",
    max: "",
    selectedProjectId: [],
    campaignDataList: [],
  });

  const {
    filterDrawer,
    trackEnabledRecording,
    durationFilter,
    statusFilter,
    callTypeFilter,
    outComefilter,
    selectedProjectId,
    campaignDataList,
  } = state;

  useEffect(() => {
    const appliedFilters = getDecryptedItem("callHistoryFilters") || {};
    const appliedFiltersLoc = appliedFilters?.[selectedAccount?._id];

    setFilterName(appliedFiltersLoc?.filterName || "");
    setDateRange([
      appliedFiltersLoc?.startDate
        ? new Date(appliedFiltersLoc?.startDate)
        : new Date(currentDate),
      appliedFiltersLoc?.endDate
        ? new Date(appliedFiltersLoc?.endDate)
        : new Date(currentDate),
    ]);

    setState((prevState) => ({
      ...prevState,
      durationFilter: appliedFiltersLoc?.durationFilter || "",
      outComefilter: appliedFiltersLoc?.outcomeFilters || [],
      statusFilter: appliedFiltersLoc?.statusFilter || [],
      callTypeFilter: appliedFiltersLoc?.callTypeFilter || [],
      min: appliedFiltersLoc?.customFilter
        ? appliedFiltersLoc?.customFilter.split("-")[0] || ""
        : "",
      max: appliedFiltersLoc?.customFilter
        ? appliedFiltersLoc?.customFilter.split("-")[1] || ""
        : "",
      selectedProjectId: appliedFiltersLoc?.selectedProjectId
        ? appliedFiltersLoc?.selectedProjectId
        : accessedProjectIds?.length > 0
        ? accessedProjectIds
        : [],
    }));
  }, [filterNameFromDropdown]);

  useEffect(() => {
    if (callHistoryDetails?.length === 0) setLoading(true);
    if (
      isReady &&
      (!(hasRestrictedCallHistoryAccess || hasAllProjectsAccess) ||
        projectList?.length > 0)
    ) {
      fetchCallHistory();
    }
  }, [currentPage, searchText, isReady, projectList]);

  useEffect(() => {
    if (callHistoryDetailsTotalCounts) {
      setTotalPages(Math.ceil(callHistoryDetailsTotalCounts / PAGE_SIZE_50));
    }
  }, [currentPage, searchText, callHistoryDetailsTotalCounts]);

  useEffect(() => {
    if (callHistoryDetails) {
      setState((prev) => ({
        ...prev,
        campaignDataList: callHistoryDetails?.map((item) => item?.combinedData),
      }));
    }
  }, [callHistoryDetails]);

  const fetchCallHistory = async () => {
    const obj = {
      pageIndex: currentPage,
      pageSize: PAGE_SIZE_50,
      live: false,
      duration:
        durationFilter === "Custom"
          ? `${state?.min}-${state?.max}`
          : durationFilter?.split(" ")[0],
      searchText: searchText,
      from: moment(startDate).format("YYYY-MM-DD"),
      to: moment(endDate).format("YYYY-MM-DD"),
      projectId:
        accessedProjectIds?.length > 0 ? accessedProjectIds : selectedProjectId,
      callStatus: statusFilter,
      callType: callTypeFilter,
      outcome: outComefilter,
      timezone,
    };

    await dispatch(getCallHistoryAction(obj));
    setLoading(false);
    await dispatch(getCallHistoryCountAction(obj));
  };

  const applyRefresh = () => {
    setLoading(true);
    setLastViewedTranscriptUUID(null);
    fetchCallHistory();
  };

  const openCallTranscriptModal = (transcriptData) => {
    setSelectedTranscript(transcriptData);
    setShowCallTranscriptModal(true);
  };

  const closeCallTranscriptModal = () => {
    setShowCallTranscriptModal(false);
    if (selectedTranscript?.call_uuid) {
      setLastViewedTranscriptUUID(selectedTranscript.call_uuid);
    }
    setSelectedTranscript(null);
  };

  const handleRedirect = (callData) => {
    if (callData?.isDeleted) {
      swalWarningMessage("Project is Deleted.");
      return;
    } else if (callData?.campaignDeleted) {
      swalWarningMessage("Campaign is deleted.");
      return;
    } else {
      axiosClient.defaults.headers.common["projectId"] = callData?.projectId;
      history.push(
        `/account/project/${callData?.projectId}/campaign-runs?campaignid=${callData?.campaignId}&campdate=${callData?.createdAt}`
      );
    }
  };

  const exportToCSV = async () => {
    setExportLoad(true);
    let obj = {
      pageIndex: 1,
      pageSize: 1000,
      live: false,
      duration:
        durationFilter === "Custom"
          ? `${state?.min}-${state?.max}`
          : durationFilter?.split(" ")[0],
      searchText: searchText,
      from: moment(startDate).format("YYYY-MM-DD"),
      to: moment(endDate).format("YYYY-MM-DD"),
      projectId: accessedProjectIds
        ? accessedProjectIds
        : selectedProjectId
        ? selectedProjectId
        : "",
      callStatus: statusFilter,
      callType: callTypeFilter,
      outcome: outComefilter,
      timezone,
    };
    let res: any = await dispatch(getCallHistoryAction({ ...obj, all: true }));
    if (res?.length <= 0) {
      swalErrorMessage("Nothing to export.");
      setExportLoad(false);
      return;
    }
    let matchArr = [
      "mandatory_params",
      "firstName",
      "lastName",
      "FirstName",
      "LastName",
      "phone",
      "PhoneNum",
    ];

    if (res) {
      let arr: any = [];
      res?.forEach((kl) => {
        let callHistory = kl?.combinedData?.campaignRunDetails;
        let copy = callHistory?.customParameters;
        let obj: any = {};
        obj["First Name"] = copy?.FirstName || "--";
        obj["Last Name"] = copy?.LastName || "--";
        obj["Phone number"] = callHistory?.phone || "--";
        obj["Call UUID"] = callHistory?.call_uuid || "";
        obj["Call Type"] =
          callHistory?.callType === "OUTBOUND_INBOUND"
            ? "SIP Inbound"
            : callHistory?.callType;
        obj["Calling Status"] =
          constantData?.callStatus[callHistory?.callStatus] || "";
        if (!hasRestrictedCallHistoryAccess) {
          obj["Cost"] = callHistory?.newCost || "";
          if (selectedAccount?.params?.pricesWaivers) {
            obj["Cost"] = "--";
          }
        }
        obj["Call Duration"] = (callHistory?.duration || 0) + " sec";
        obj["Recording Url"] = callHistory?.callRecordingUrl || "";

        if (!isEmpty(copy)) {
          for (const customKeys in copy) {
            if (!matchArr.includes(customKeys) && customKeys !== "call_type") {
              obj[customKeys] = copy[customKeys];
            }
          }
        }

        if (!hasRestrictedCallHistoryAccess) {
          obj["Project Name"] = kl?.combinedData?.projectName || "";
          obj["Campaign Name"] = kl?.combinedData?.campaignName || "";
          obj["Campaign Run Time"] =
            converTime(kl?.combinedData?.createdAt) || "";
        }

        obj["Call Time"] = converTime(callHistory?.createdAt) || "";

        arr.push(obj);
      });
      const fileType =
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
      const fileExtension = ".xlsx";
      const ws = XLSX.utils.json_to_sheet(arr);
      const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const data = new Blob([excelBuffer], { type: fileType });
      FileSaver.saveAs(data, `Call History Report` + fileExtension);
    }
    await dispatch(
      getCallHistoryAction({
        ...obj,
        pageSize: PAGE_SIZE_50,
        pageIndex: currentPage,
      })
    );
    setExportPopup(true);
    setExportLoad(false);
    setCurrentPage(1);
  };

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const toggle = () => {
    const appliedFilters = getDecryptedItem("callHistoryFilters") || {};
    const appliedFiltersLoc = appliedFilters?.[selectedAccount?._id];
    setState((prev) => ({
      ...prev,
      filterDrawer: !prev.filterDrawer,
      durationFilter: appliedFiltersLoc?.durationFilter || "",
      outComefilter: appliedFiltersLoc?.outcomeFilters || [],
      statusFilter: appliedFiltersLoc?.statusFilter || [],
      callTypeFilter: appliedFiltersLoc?.callTypeFilter || [],
      min: appliedFiltersLoc?.customFilter
        ? appliedFiltersLoc?.customFilter.split("-")[0] || ""
        : "",
      max: appliedFiltersLoc?.customFilter
        ? appliedFiltersLoc?.customFilter.split("-")[1] || ""
        : "",
      selectedProjectId: appliedFiltersLoc?.selectedProjectId || "",
    }));
    setFilterName(appliedFiltersLoc?.filterName || "");
    setDateRange([
      appliedFiltersLoc?.startDate
        ? new Date(appliedFiltersLoc?.startDate)
        : new Date(currentDate),
      appliedFiltersLoc?.endDate
        ? new Date(appliedFiltersLoc?.endDate)
        : new Date(currentDate),
    ]);
  };

  const exportToggle = () => {
    setExportPopup((prev) => !prev);
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };
  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const getName = (phone) => {
    const firstName = phone?.customParameters?.firstName || "";
    const lastName = phone?.customParameters?.lastName || "";
    return `${firstName} ${lastName}`.trim() || "No Name";
  };

  const obj = {
    searchText,
    setMainState: setState,
    trackEnabledRecording,
    mainState: state,
    startDate,
    endDate,
    currentDate,
    setDateRange,
    filterName,
    setFilterName,
    setFilterNameFromDropdown,
    setSearchTerm,
    setCurrentPage,
    selectedProjectId
  };

  return (
    <Box sx={{ py: 1, position: "relative" }}>
      <CallHistoryHeader
        setIsReady={setIsReady}
        setSearchText={setSearchText}
        exportToCSV={exportToCSV}
        toggleFilterDrawer={toggle}
        totalCalls={callHistoryDetailsTotalCounts}
        exportLoad={exportLoad}
        setSearchTerm={setSearchTerm}
        searchTerm={searchTerm}
        applyRefresh={applyRefresh}
        loading={loading}
        setCurrentPage={setCurrentPage}
      />
      <DynamicTable
        columns={[
          {
            label: "Phone Number",
            render: ({ campaignRunDetails }) => {
              const callType = campaignRunDetails?.callType;
              return (
                <Box className="flex-align-center" gap={1.3}>
                  <Tooltip
                    title={getName(campaignRunDetails)}
                    placement="bottom"
                    arrow
                  >
                    <span>{shortenString(campaignRunDetails?.phone, 15)}</span>
                  </Tooltip>
                  {callType && <CallDirectionIcon callType={callType} />}
                </Box>
              );
            },
          },
          {
            label: "Call Status | Ended By",
            render: (row) => {
              const statusData = getCallStatusData(
                row?.campaignRunDetails?.callStatus,
                constantData
              );
              return (
                <Box className="flex-xy-center" gap={1} mr={5}>
                  <StatusButton
                    status={statusData?.status}
                    statusColor={statusData?.statusColor}
                    backgroundColor={statusData?.backgroundColor}
                    statusDescription={statusData?.statusDescription}
                  />
                  <Divider orientation="vertical" flexItem />
                  <Typography className="font-12-mediumgrey">
                    {row?.campaignRunDetails?.botDropped == null ? (
                      "--"
                    ) : row?.campaignRunDetails?.botDropped ? (
                      <Tooltip title="Call Ended by Bot">
                        <Bot color="rgba(0, 0, 0, 0.5)" size={20} />
                      </Tooltip>
                    ) : (
                      <Tooltip title="Call Ended by Human">
                        <User color="rgba(0, 0, 0, 0.5)" size={20} />
                      </Tooltip>
                    )}
                  </Typography>
                </Box>
              );
            },
          },
          !hasRestrictedCallHistoryAccess && {
            label: "Cost",
            render: ({ campaignRunDetails }) =>
              selectedAccount?.params?.pricesWaivers
                ? "--"
                : campaignRunDetails?.newCost || "--",
          },
          {
            label: "UUID",
            render: (row) => {
              return (
                <Box className="flex-xy-center gap-0.5">
                  <Typography className="font-12-mediumgrey">
                    {row?.campaignRunDetails?.call_uuid}
                  </Typography>
                  <Tooltip title="Copy UUID">
                    <IconButton
                      size="small"
                      onClick={() => {
                        copy(row?.campaignRunDetails?.call_uuid);
                        setCopyAlert({ open: true, message: "UUID copied!" });
                      }}
                    >
                      {" "}
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              );
            },
            styles: { textTransform: "none", padding: 2 },
          },
          {
            label: "Recording",
            render: (row) => (
              <Box className="flex-xy-center">
                <ReactAudioPlayer
                  src={
                    row?.campaignRunDetails?.callRecordingUrl ||
                    `https://storage.googleapis.com/botstream-recordings/${row?.campaignRunDetails?.call_uuid}.mp3`
                  }
                  controls
                  style={{
                    maxWidth: "250px",
                    height: "30px",
                  }}
                />
              </Box>
            ),
          },
          trunk === "show" && {
            label: "Trunk Status | Trunk Status Code",
            render: ({ campaignRunDetails }) => {
              return (
                <Box className="flex-xy-center" gap={0.5} mr={10}>
                  <Typography className="font-12-mediumgrey">
                    {campaignRunDetails?.pbxStatusText || "--"}
                  </Typography>
                  <Divider orientation="vertical" flexItem />
                  <Typography className="font-12-mediumgrey">
                    {campaignRunDetails?.pbxStatusCode || "--"}
                  </Typography>
                </Box>
              );
            },
          },
          {
            label: "Actions",
            render: (row) => (
              <>
                <BaseButton
                  // disabled={
                  //   !["703", "712"].includes(
                  //     row?.campaignRunDetails?.callStatus
                  //   )
                  // }
                  title="View Transcript"
                  handleClick={() =>
                    openCallTranscriptModal(row?.campaignRunDetails)
                  }
                  styles={{ color: "#4280FF" }}
                />

                <CallHistoryPopover
                  hasRestrictedCallHistoryAccess={
                    !hasRestrictedCallHistoryAccess
                  }
                  callData={row}
                />
                {!hasRestrictedCallHistoryAccess && (
                  <IconButton
                    title="Redirect"
                    onClick={() => handleRedirect(row)}
                  >
                    <RedirectIcon />
                  </IconButton>
                )}

                {selectedAccount?.params?.logAccess?.includes(Stalker) && (
                  <button
                    className="custom-button-icon"
                    title="Logs"
                    onClick={() => {
                      setCall_uuid(row?.campaignRunDetails?.call_uuid);
                      setShow(true);
                    }}
                  >
                    Logs
                  </button>
                )}
              </>
            ),
          },
        ]?.filter(Boolean)}
        data={campaignDataList}
        loading={loading}
        totalPages={totalPages}
        currentPage={currentPage}
        handlePageChange={handlePageChange}
        handlePrevious={handlePrevious}
        handleNext={handleNext}
        noDataAvailableMessage="No Call History Available"
        totalRowCount={callHistoryDetailsTotalCounts}
        rowCount={campaignDataList?.length}
        highlightRowColor={{
          backgroundColor: "#e4e7ecff",
          "&:hover": {
            backgroundColor: "#e4e7ecff",
          },
        }}
        highlightRowCondition={(row) =>
          row?.campaignRunDetails?.call_uuid === lastViewedTranscriptUUID
        }
      />

      {showCallTranscriptModal && (
        <CallTranscriptModal
          show={showCallTranscriptModal}
          onClose={closeCallTranscriptModal}
          selectedTranscript={selectedTranscript}
        />
      )}

      {filterDrawer && (
        <Drawer anchor={"right"} open={filterDrawer} onClose={toggle}>
          <FilterBody
            obj={obj}
            encryptionKey="callHistoryFilters"
            showSection={true}
            main={true}
            all={false}
          />
        </Drawer>
      )}
      {call_uuid && (
        <CallLogs
          call_uuid={call_uuid}
          show={show}
          toggle={() => {
            setCall_uuid("");
            setShow(false);
          }}
        />
      )}
      {exportPopup && (
        <CommonPopup
          message={`The selected data are exported successfully`}
          show={exportPopup}
          toggle={exportToggle}
          Icon={ExportFileIcon}
          heading="File exported successfully"
        />
      )}
      <CopyAlert
        open={copyAlert.open}
        onClose={() => setCopyAlert({ ...copyAlert, open: false })}
        alertMessage={copyAlert.message}
        styles={{ mt: 7 }}
      />
    </Box>
  );
};

export default CallHistory;
